import React, { useState, useEffect, useMemo } from 'react';
import { Account } from './services/Account'; // Correct path relative to src/app/
import { PlayFab } from './services/PlayFab'; // Correct path relative to src/app/
import { RangeDisplay } from './components/RangeDisplay'; // Import the new component
import './ChartsPage.css'; // Import CSS for styling

// Define the structure of the chart data
interface PositionHands {
  [position: string]: string[];
}

interface GameTypeCharts {
  [gameType: string]: PositionHands;
}

// Define the expected structure of the CloudScript result
interface CloudScriptResult {
  FunctionResult?: {
    success?: boolean;
    charts?: GameTypeCharts;
    error?: string;
  };
  // Add other potential top-level fields from PlayFab response if needed
}

const ChartsPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(Account.isLoggedIn());
  const [chartsData, setChartsData] = useState<GameTypeCharts | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGameType, setSelectedGameType] = useState<string>('');
  // State for the currently selected position tab
  const [selectedPosition, setSelectedPosition] = useState<string>(''); 

  // Memoize available positions based on selected game type
  const availablePositions = useMemo(() => {
    if (chartsData && selectedGameType && chartsData[selectedGameType]) {
      return Object.keys(chartsData[selectedGameType]);
    }
    return [];
  }, [chartsData, selectedGameType]);

  // Effect for login check and data fetching
  useEffect(() => {
    const checkLoginAndFetch = async () => {
      const loggedIn = Account.isLoggedIn();
      setIsLoggedIn(loggedIn);

      if (!loggedIn) {
        // Reset state if not logged in
        setError("Please log in to view the charts.");
        setChartsData(null);
        setLoading(false);
        setSelectedGameType('');
        setSelectedPosition('');
        return;
      }

      // Start loading if logged in
      setError(null);
      setLoading(true);

      try {
        const result = await PlayFab.executeCloudScript('getPokerCharts', {});
        
        if (
          result.success &&
          result.data?.FunctionResult?.FunctionResult?.success &&
          result.data.FunctionResult.FunctionResult.charts
        ) {
          const fetchedCharts = result.data.FunctionResult.FunctionResult.charts as GameTypeCharts;
          setChartsData(fetchedCharts);
          const gameTypes = Object.keys(fetchedCharts);
          
          let currentSelectedGameType = selectedGameType;
          // Set default game type if not set or invalid
          if (gameTypes.length > 0 && (!selectedGameType || !fetchedCharts[selectedGameType])) {
            currentSelectedGameType = gameTypes[0];
            setSelectedGameType(currentSelectedGameType);
          }

          // Set default position based on the valid game type
          if (currentSelectedGameType && fetchedCharts[currentSelectedGameType]) {
              const positionsForType = Object.keys(fetchedCharts[currentSelectedGameType]);
              if (positionsForType.length > 0 && (!selectedPosition || !fetchedCharts[currentSelectedGameType][selectedPosition])) {
                  setSelectedPosition(positionsForType[0]); // Set first position as default
              }
          } else {
              setSelectedPosition(''); // Clear position if game type is invalid
          }

        } else {
          // Handle fetch errors
          const errorMsg = result.data?.FunctionResult?.FunctionResult?.error || result.data?.Error?.Message || 'Failed to fetch charts data.';
          console.error("Chart fetch condition failed.", { /*...*/ }); 
          setError(errorMsg);
          setChartsData(null);
          setSelectedPosition(''); 
        }
      } catch (err: any) {
        // Handle exceptions during fetch
        console.error('Error calling getPokerCharts:', err);
        setError(err.message || 'An error occurred while fetching charts.');
        setChartsData(null);
        setSelectedPosition('');
      } finally {
        setLoading(false);
      }
    };

    checkLoginAndFetch();

    // Listener for auth changes (e.g., logout)
    const handleAuthChange = () => {
      const currentlyLoggedIn = Account.isLoggedIn();
      if (isLoggedIn !== currentlyLoggedIn) {
        setIsLoggedIn(currentlyLoggedIn);
        checkLoginAndFetch(); // Refetch or clear data on auth change
      }
    };
    window.addEventListener('authChange', handleAuthChange);
    return () => window.removeEventListener('authChange', handleAuthChange);

  }, [isLoggedIn]); // Rerun only when login status changes

  // Update game type selection, reset position
  const handleGameTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newGameType = event.target.value;
    setSelectedGameType(newGameType);
    // Reset position - the useEffect will set the default for the new game type
    setSelectedPosition(''); 
  };

  // Update position selection
  const handlePositionChange = (position: string) => {
      setSelectedPosition(position);
  };

  // Memoize the current range to display based on selections
  const currentRange = useMemo(() => {
      if (chartsData && selectedGameType && selectedPosition && chartsData[selectedGameType]?.[selectedPosition]) {
          // Ensure `hands` is an array before creating Set
          const hands = chartsData[selectedGameType][selectedPosition];
          return Array.isArray(hands) ? new Set(hands) : new Set<string>(); 
      }
      return new Set<string>(); // Return empty set if data/selection is missing
  }, [chartsData, selectedGameType, selectedPosition]);

  return (
    <div className="charts-page-container">
      <h1>Poker Opening Charts</h1>

      {/* Loading/Error/Login Messages */} 
      {loading && <div className="loading-indicator">Loading charts...</div>}
      {error && <div className="error-message">Error: {error}</div>}
      {!isLoggedIn && !loading && (
        <div className="warning-message">Please log in to view the charts.</div>
      )}

      {/* Main Content Area */} 
      {isLoggedIn && !loading && !error && chartsData && (
        <div className="charts-content">
          
          {/* Game Type Selector */} 
          <div className="game-type-selector">
            <label htmlFor="game-type-select">Select Game Type: </label>
            <select
              id="game-type-select"
              value={selectedGameType}
              onChange={handleGameTypeChange}
              disabled={Object.keys(chartsData).length === 0}
            >
              {Object.keys(chartsData).map((gameType) => (
                <option key={gameType} value={gameType}>
                  {gameType.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Position Selector Buttons */} 
          {availablePositions.length > 0 && (
            <div className="position-selector">
              {availablePositions.map((pos) => (
                <button 
                  key={pos} 
                  onClick={() => handlePositionChange(pos)}
                  // Apply 'active' class if this position is selected
                  className={`position-button ${selectedPosition === pos ? 'active' : ''}`}
                >
                  {pos}
                </button>
              ))}
            </div>
          )}

          {/* Single Range Display Container */} 
          {selectedPosition ? (
            <div className="single-range-display-container">
              {/* Render the single RangeDisplay with the calculated current range */}
              <RangeDisplay range={currentRange} /> 
            </div>
          ) : (
            // Optional: Message if positions are available but none selected yet
            availablePositions.length > 0 && <div className="warning-message">Select a position above.</div> 
          )}

        </div>
      )}
      
      {/* Fallback message if logged in but data failed */}
      {isLoggedIn && !loading && !error && !chartsData && (
        <div className="warning-message">Could not load chart data.</div>
      )}
    </div>
  );
};

export default ChartsPage; 